import { useAuthenticator } from "@aws-amplify/ui-react";
import cn from "classnames";
import Link from "next/link";
import { useRouter } from "next/router";
import { ReactNode, useMemo } from "react";
import styled from "styled-components";

// Types
import { getRankName, isAtLeast, Rank, User } from "../types";

// State
import { useAppSelector } from "../app/hooks";
import {
  selectSignedInUserFullName,
  selectSignedInUserRank,
} from "../app/signedInUserSlice";

// Components
import { ActiveLink } from "./ActiveLink";
import { Logo } from "./Logo";
import * as Bootstrap from "./Bootstrap";

export const Header = () => {
  const router = useRouter();

  const { signOut, authStatus } = useAuthenticator((context) => [
    context.authStatus,
  ]);

  const name = useAppSelector(selectSignedInUserFullName);
  const rank = useAppSelector(selectSignedInUserRank);
  const atLeast = isAtLeast(rank);

  const authenticatedElems = useMemo<ReactNode>(
    () => (
      <>
        <div
          className={cn(
            "d-flex align-items-md-end flex-column me-md-2 text-white-50 fw-light py-4 py-md-2 lh-1",
            !atLeast(Rank.Admin) &&
              "border-1 border-secondary border-top border-md-0"
          )}
        >
          <strong className="fw-normal text-white">{name}</strong>
          <Styled.div.rank
            rank={rank}
            className="d-flex align-items-center text-lowercase"
          >
            {getRankName(rank)}
          </Styled.div.rank>
        </div>
        <Styled.Bootstrap.Nav.Link
          onClick={signOut}
          as={Bootstrap.Button}
          size="sm"
          variant="outline-secondary"
          className="is-signout nav-link mb-3 mb-md-0"
        >
          Sign out
        </Styled.Bootstrap.Nav.Link>
      </>
    ),
    [atLeast, name, rank, signOut]
  );

  const unauthenticatedElem = useMemo<ReactNode>(
    () => (
      <Link
        href={`/sign-in?from=${encodeURIComponent(router.asPath)}`}
        passHref
      >
        <Styled.Bootstrap.Nav.Link>Sign In</Styled.Bootstrap.Nav.Link>
      </Link>
    ),
    [router.asPath]
  );

  const rightElem = useMemo<ReactNode>(
    () =>
      authStatus === "authenticated" ? authenticatedElems : unauthenticatedElem,
    [authStatus, authenticatedElems, unauthenticatedElem]
  );

  const auditLinkElem = useMemo<ReactNode>(
    () =>
      atLeast(Rank.Admin) ? (
        <ActiveLink href="/admin/audit" passHref>
          <Styled.Bootstrap.Nav.Link
            $activeColor="var(--bt-primary-bg-gold)"
            className="d-flex align-items-center px-2 ms-lg-1"
          >
            View Audit
          </Styled.Bootstrap.Nav.Link>
        </ActiveLink>
      ) : null,
    [atLeast]
  );

  const adminLinkElem = useMemo<ReactNode>(
    () =>
      atLeast(Rank.Admin) ? (
        <ActiveLink href="/admin/users" passHref>
          <Styled.Bootstrap.Nav.Link
            $activeColor="var(--bt-primary-bg-gold)"
            className="d-flex align-items-center px-2 ms-lg-1"
          >
            Manage Users
          </Styled.Bootstrap.Nav.Link>
        </ActiveLink>
      ) : null,
    [atLeast]
  );

  const signKeyLinkElem = useMemo<ReactNode>(
    () =>
      atLeast(Rank.Signer) ? (
        <ActiveLink href="/key/sign" passHref>
          <Styled.Bootstrap.Nav.Link>Sign Key</Styled.Bootstrap.Nav.Link>
        </ActiveLink>
      ) : null,
    [atLeast]
  );

  return (
    <Bootstrap.Navbar
      as="header"
      variant="dark"
      bg="primary"
      sticky="top"
      expand="md"
      className="flex-md-nowrap p-0"
    >
      <Bootstrap.Container>
        <Styled.Bootstrap.Navbar.Brand
          href="/"
          className="d-flex align-items-center fs-0"
          aria-label="Go to the home page"
        >
          <Logo size={45} color="white" />
        </Styled.Bootstrap.Navbar.Brand>
        <Styled.Bootstrap.Navbar.Toggle aria-controls="responsive-navbar-nav" />
        <Bootstrap.Navbar.Collapse id="responsive-navbar-nav">
          <Bootstrap.Nav className="me-auto d-flex align-items-lg-center">
            <ActiveLink href="/" passHref>
              <Styled.Bootstrap.Nav.Link>Home</Styled.Bootstrap.Nav.Link>
            </ActiveLink>
            {signKeyLinkElem}
            {adminLinkElem}
            {auditLinkElem}
          </Bootstrap.Nav>
          <Bootstrap.Nav className="align-items-lg-center">
            {rightElem}
          </Bootstrap.Nav>
        </Bootstrap.Navbar.Collapse>
      </Bootstrap.Container>
    </Bootstrap.Navbar>
  );
};

const Styled = {
  div: {
    rank: styled.div<Pick<User, "rank">>`
      color: ${({ color, rank }) => {
        const atLeast = isAtLeast(rank);

        switch (true) {
          case !!color:
            return color;
          case atLeast(Rank.SuperAdmin):
            return `var(--bt-primary-bg-gold)`;
        }
      }};

      font-size: 0.7em;
      font-weight: 400;
      margin-top: 2px;
      line-height: 1.2;
    `,
  },

  // Components
  Bootstrap: {
    Nav: {
      Link: styled(Bootstrap.Nav.Link)<{ $activeColor?: string }>`
        --active-color: ${(props) => props.$activeColor || "#ffffff"};
        --bs-nav-link-color: var(--active-color);
        --bs-navbar-active-color: var(--active-color);

        position: relative;

        &.active:after {
          content: "";
          position: absolute;
          bottom: 0;
          left: 0;
          display: block;
          width: 100%;
          height: 2px;
          background-color: var(--active-color);
        }

        &.is-signout {
          --bs-btn-border-color: var(--bt-primary-75);
          --bs-btn-hover-bg: var(--bt-primary-85);
          --bs-btn-hover-border-color: var(--bt-primary-85);
          --bs-btn-active-bg: var(--bt-primary-85);
          --bs-btn-active-border-color: var(--bt-primary-85);
        }
      `,
    },
    Navbar: {
      Toggle: styled(Bootstrap.Navbar.Toggle)`
        top: 0.25rem;
        right: 1rem;
      `,
      Brand: styled(Bootstrap.Navbar.Brand)`
        padding-top: 0.75rem;
        padding-bottom: 0.75rem;
      `,
    },
  },
};
